import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import * as React from "react"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"
import type { components } from "@/types/api.gen"

import { pageToWire, severityToWire } from "./adapter"

import {
  AuthorSide,
  type Authority,
  type Evidence,
  type Finding,
  type FindingAxes,
  type FindingGroup,
  type FindingId,
  type FindingSeverity,
  type Message,
  type MessageId,
  type PointRect,
  type Review,
  type ReviewId,
  type ReviewStage,
  type Role,
  type UserId,
} from "../data/review-data"

import { useMutation, type MutationResult } from "./use-mutation"
import {
  currentUserId,
  toMessage,
  type CommentWire,
  type ConversationData,
} from "./use-review-comments"
import type { WireFindingState } from "./wire"

/**
 * Review mutations. Each hook follows the same three-step lifecycle:
 *
 *   1. `onMutate` snapshots the current cache entry and applies the
 *      optimistic patch.
 *   2. `mutationFn` performs the network call under an AbortSignal.
 *   3. `onError` restores the snapshot so the UI reflects the server's
 *      view even after a failure or a client-side abort.
 *
 * Optimistic writes go to `qk.reviews.detail(id)` — the query the page reads —
 * so a change is visible the moment it is made rather than on the next fetch.
 * Direct mutation of `Review` values is forbidden; that single cache is what
 * keeps optimistic updates, live invalidations and rollback consistent.
 */

function localId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

interface Ctx {
  previous: Review | null
}

/**
 * Apply an optimistic change to the review the page is rendering.
 *
 * Writes through React Query rather than a store of its own: `useReviewQuery`
 * reads `qk.reviews.detail(id)`, so a change to the findings rail, the verdict
 * or the status has to reach this cache to be seen.
 *
 * It is *not* the only conversation-bearing cache. The messages panel reads
 * `qk.reviews.comments` via `useReviewCommentsQuery`; a message patched in here
 * alone renders nowhere. Use `patchConversation` for that.
 */
function patchReview(
  qc: QueryClient,
  reviewId: ReviewId,
  patch: (prev: Review) => Review,
): { previous: Review | null } {
  const key = qk.reviews.detail(reviewId)
  const previous = qc.getQueryData<Review | null>(key) ?? null
  if (previous) qc.setQueryData<Review | null>(key, patch(previous))
  return { previous }
}

/** Put back what was there before the request failed. */
function rollback(qc: QueryClient, reviewId: ReviewId, previous: Review | null): void {
  if (previous) qc.setQueryData<Review | null>(qk.reviews.detail(reviewId), previous)
}

/**
 * Apply an optimistic change to the conversation the messages panel renders.
 *
 * The sibling of `patchReview` for `qk.reviews.comments`. Returns the previous
 * value so a caller can restore it, and does nothing when the cache is cold —
 * there is no panel to move in that case, and seeding a partial conversation
 * would have the next read mistake it for the whole one.
 */
function patchConversation(
  qc: QueryClient,
  reviewId: ReviewId,
  patch: (prev: ConversationData) => ConversationData,
): ConversationData | null {
  const key = qk.reviews.comments(reviewId)
  const previous = qc.getQueryData<ConversationData>(key) ?? null
  if (previous) qc.setQueryData<ConversationData>(key, patch(previous))
  return previous
}

/* ── Verdict ───────────────────────────────────────────────────────────── */

interface SmeVerdictBody {
  decision: "confirm" | "reject" | "modify"
  note?: string
}

/**
 * The UI axis mapped onto what the endpoint accepts.
 *
 * Null for the two the backend cannot record: `needs_sme` is derived from
 * `expected_system_output` rather than a decision, and there is no call that
 * clears a verdict back to `unset`.
 */
function decisionFor(verdict: FindingAxes["verdict"]): SmeVerdictBody["decision"] | null {
  if (verdict === "confirmed") return "confirm"
  if (verdict === "rejected") return "reject"
  return null
}

export interface SetVerdictVars {
  reviewId: ReviewId
  findingId: FindingId
  verdict: FindingAxes["verdict"]
}

/**
 * Verdict toggle. The exposed `undo` reverses the previous mutation's
 * effect by writing the prior verdict back; this is what "Undo" in the
 * toast bar reaches for.
 *
 * The mutation itself follows the toggle semantic from the spec:
 * repeating a verdict clears it to `unset`. Callers therefore should
 * not implement their own toggle logic on top of `mutate`.
 */
export function useSetVerdictMutation(): MutationResult<SetVerdictVars> & {
  undo: (vars: { reviewId: ReviewId; findingId: FindingId }) => void
} {
  const qc = useQueryClient()
  const lastPrevRef = React.useRef<Map<string, FindingAxes["verdict"]>>(new Map())

  const mutation = useMutation<SetVerdictVars, Ctx>({
    mutationFn: async ({ reviewId, findingId, verdict }, signal) => {
      const decision = decisionFor(verdict)
      // `needs_sme` and clearing a verdict have no endpoint: the escalation flag
      // lives on `expected_system_output`, which this route does not set. The
      // optimistic change still shows, and the next fetch corrects it — which is
      // the honest behaviour until the backend offers a way to record either.
      if (!decision) return
      await http.post<unknown, SmeVerdictBody>(
        `/api/v1/reviews/${String(reviewId)}/findings/${String(findingId)}/verdict`,
        { decision },
        { signal },
      )
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onMutate: ({ reviewId, findingId, verdict }) => {
      const { previous } = patchReview(qc, reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) => {
          if (f.id !== findingId) return f
          lastPrevRef.current.set(`${reviewId}:${findingId}`, f.axes.verdict)
          const next = f.axes.verdict === verdict ? "unset" : verdict
          return { ...f, axes: { ...f.axes, verdict: next } }
        }),
      }))
      return { previous }
    },
    onError: (_err, vars, ctx) => {
      rollback(qc, vars.reviewId, ctx?.previous ?? null)
    },
  })

  const undo = React.useCallback(
    ({ reviewId, findingId }: { reviewId: ReviewId; findingId: FindingId }) => {
      const key = `${reviewId}:${findingId}`
      const prev = lastPrevRef.current.get(key)
      if (prev === undefined) return
      lastPrevRef.current.delete(key)
      patchReview(qc, reviewId, (r) => ({
        ...r,
        findings: r.findings.map((f) =>
          f.id === findingId ? { ...f, axes: { ...f.axes, verdict: prev } } : f,
        ),
      }))
    },
    [],
  )

  return { ...mutation, undo }
}

/* ── Mark corrected ────────────────────────────────────────────────────── */

export interface MarkCorrectedVars {
  reviewId: ReviewId
  findingId: FindingId
}

/**
 * Confirms a proposed correction. The state axis only permits the
 * transition `candidate_corrected → corrected_confirmed`; the mutation
 * is a no-op for findings in any other state. Do not use this hook to
 * force earlier transitions — the pipeline owns them.
 */
export function useMarkCorrectedMutation(): MutationResult<MarkCorrectedVars> {
  const qc = useQueryClient()

  return useMutation<MarkCorrectedVars, Ctx>({
    mutationFn: async ({ reviewId, findingId }, signal) => {
      await http.post<unknown, { state: WireFindingState }>(
        `/api/v1/reviews/${String(reviewId)}/findings/${String(findingId)}/state`,
        { state: "corrected_confirmed" },
        { signal },
      )
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onMutate: ({ reviewId, findingId }) => {
      const { previous } = patchReview(qc, reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) => {
          if (f.id !== findingId) return f
          if (f.axes.state !== "candidate_corrected") return f
          return { ...f, axes: { ...f.axes, state: "corrected_confirmed" } }
        }),
      }))
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(qc, vars.reviewId, ctx?.previous ?? null),
  })
}

/* ── Edit narrative ────────────────────────────────────────────────────── */

export interface EditNarrativeVars {
  reviewId: ReviewId
  findingId: FindingId
  patch: {
    claim?: string
    reasoning?: string | null
    remediation?: string | null
    divergence?: Evidence | null
    authorities?: Authority[]
  }
}

export function useEditNarrativeMutation(): MutationResult<EditNarrativeVars> {
  const qc = useQueryClient()

  return useMutation<EditNarrativeVars, Ctx>({
    mutationFn: async ({ reviewId, findingId, patch }, signal) => {
      // The route carries the client-facing summary and nothing else. The edit
      // form offers only that field for the same reason — the other patch keys
      // exist for when the API accepts them.
      if (patch.claim === undefined) return
      await http.patch<unknown, { summary: string }>(
        `/api/v1/reviews/${String(reviewId)}/findings/${String(findingId)}/summary`,
        { summary: patch.claim },
        { signal },
      )
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onMutate: ({ reviewId, findingId, patch }) => {
      const { previous } = patchReview(qc, reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) => {
          if (f.id !== findingId) return f
          return {
            ...f,
            claim: patch.claim ?? f.claim,
            claimIsOverridden: patch.claim != null || f.claimIsOverridden,
            reasoning: patch.reasoning === undefined ? f.reasoning : patch.reasoning,
            remediation: patch.remediation === undefined ? f.remediation : patch.remediation,
            divergence: patch.divergence === undefined ? f.divergence : patch.divergence,
            authorities: patch.authorities ?? f.authorities,
          }
        }),
      }))
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(qc, vars.reviewId, ctx?.previous ?? null),
  })
}

/* ── Add finding ───────────────────────────────────────────────────────── */

const GROUP_LOOKUP: Record<"g-text" | "g-format", FindingGroup> = {
  "g-text": { id: "g-text", label: "Text review", ordinal: 3 },
  "g-format": { id: "g-format", label: "Formatting review", ordinal: 4 },
}

/**
 * Reviewer-authored finding. Mirrors `ManualFindingCreate` in
 * pyxis/api/src/pyxisrc_ai/schemas/finding_io.py with two shape
 * adjustments the wire mapping absorbs:
 *
 *   • `severity` on the wire is `"error" | "warn"`; the FE keeps the
 *     richer `"error" | "warning" | "info"` and maps at the seam
 *     (`warning → warn`, `info → error`) until the backend widens.
 *   • `divergence` on the wire is a plain string; the FE keeps the
 *     structured `{master, printed}` because the read views need both
 *     halves. The wire mapper serializes as `Master: X / FPL: Y`.
 *
 * `occurrences` is derived at the seam from `bbox` + `pageHint`: one
 * occurrence with the drawn box when we have one, empty otherwise.
 */
/**
 * The request body for a hand-authored finding, taken from the published
 * schema rather than restated here: a hand-written copy drifts silently, and
 * this one did — it declared `citation` a string where the API takes a list,
 * which TypeScript could not catch and the reviewer met as a 422.
 */
type ManualFindingBody = components["schemas"]["ManualFindingCreate"]

export interface AddFindingVars {
  reviewId: ReviewId
  input: {
    ruleId: string
    severity: FindingSeverity
    claim: string
    reasoning: string | null
    remediation: string | null
    authorities: Authority[]
    bbox: PointRect | null
    pageHint: number | null
    groupId: "g-text" | "g-format"
    /** Named page region resolved from the drawn box, if any. */
    sectionId: string | null
    divergence: Evidence | null
  }
}

export function useAddFindingMutation(): MutationResult<AddFindingVars> & {
  lastCreatedId: FindingId | null
} {
  const qc = useQueryClient()
  const [lastCreatedId, setLastCreatedId] = React.useState<FindingId | null>(null)

  const mutation = useMutation<AddFindingVars, Ctx>({
    mutationFn: async ({ reviewId, input }, signal) => {
      // A manual finding has to land somewhere on the page: the endpoint
      // requires at least one occurrence, and the drawn box is what provides it.
      if (!input.bbox) return
      const { page, x0, y0, x1, y1 } = input.bbox
      await http.post<unknown, ManualFindingBody>(
        `/api/v1/reviews/${String(reviewId)}/findings`,
        {
          rule_id: input.ruleId,
          message: input.claim,
          severity: severityToWire(input.severity),
          occurrences: [{ side: "fpl", page: pageToWire(page), bbox: [x0, y0, x1, y1] }],
          ...(input.sectionId === null ? {} : { section_id: input.sectionId }),
          ...(input.reasoning === null ? {} : { reasoning: input.reasoning }),
          ...(input.remediation === null ? {} : { remediation: input.remediation }),
          ...(input.authorities.length > 0
            ? { citation: input.authorities.map((a) => a.label) }
            : {}),
        },
        { signal },
      )
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onMutate: ({ reviewId, input }) => {
      const now = new Date().toISOString()
      const id = `f-${localId()}` as FindingId
      setLastCreatedId(id)
      const { previous } = patchReview(qc, reviewId, (prev) => {
        const nextNumber = prev.findings.reduce((m, f) => Math.max(m, f.number), 0) + 1
        const finding: Finding = {
          id,
          number: nextNumber,
          ruleId: input.ruleId,
          // A reviewer's own finding answers to no pipeline section; the
          // server will fill one in if it can place it on the next fetch.
          sectionId: null,
          severity: input.severity,
          claim: input.claim,
          claimIsOverridden: false,
          placement: input.bbox ? "box" : "document",
          bbox: input.bbox,
          pageHint: input.pageHint,
          occurrences: 1,
          occurrenceRects: input.bbox ? [input.bbox] : [],
          // A reviewer's own finding answers to no KPI: those are a property of
          // the rule the pipeline matched, not of the observation.
          kpis: [],
          axes: {
            verdict: "unset",
            state: "open",
            clientResolution: "none",
            origin: "manual",
          },
          confidence: null,
          authoredBy: { name: "You", at: now },
          group: GROUP_LOOKUP[input.groupId],
          kind: "judgment",
          divergence: input.divergence,
          reasoning: input.reasoning,
          remediation: input.remediation,
          authorities: input.authorities,
          firstSeenVersion: prev.version,
          commentCount: 0,
          unreadCount: 0,
          smeNote: null,
          dismissalNote: null,
        }
        return { ...prev, findings: [...prev.findings, finding] }
      })
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(qc, vars.reviewId, ctx?.previous ?? null),
  })

  return { ...mutation, lastCreatedId }
}

/* ── Assignment ──────────────────────────────────────────────────────────
 * These mirror the backend workflow endpoints one-for-one:
 *   POST /reviews/:id/assign         — assign or reassign
 *   POST /reviews/:id/throwback      — return to pool
 *   POST /reviews/:id/advance-stage  — initial → secondary → done
 *   POST /reviews/:id/archive        — archive; last stage is preserved
 *
 * Each mutation appends one AssignmentEvent to the trail. Actions are
 * distinct: an "assign" over an empty assignee reads differently than a
 * "reassign" that displaces someone. The trail records both.
 */

export interface AssignVars {
  reviewId: ReviewId
  assigneeId: UserId
  assigneeName: string
  assigneeRole: Role
  note: string | null
}

export function useAssignReviewMutation(): MutationResult<AssignVars> {
  const qc = useQueryClient()
  return useMutation<AssignVars, Ctx>({
    mutationFn: async ({ reviewId, assigneeId, note }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/assign`,
        { user_id: Number(assigneeId), note },
        { signal },
      )
    },
    onSuccess: (_data, { reviewId }) => {
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onMutate: ({ reviewId, assigneeId, assigneeName, assigneeRole, note }) => {
      const now = new Date().toISOString()
      const { previous } = patchReview(qc, reviewId, (prev) => {
        const wasAssigned = prev.assignment.assignee !== null
        const fromUser = prev.assignment.assignee?.name ?? null
        return {
          ...prev,
          assignment: {
            ...prev.assignment,
            assignee: {
              userId: assigneeId,
              name: assigneeName,
              role: assigneeRole,
              sinceISO: now,
            },
            events: [
              ...prev.assignment.events,
              {
                action: wasAssigned ? "reassign" : "assign",
                at: now,
                actor: "You",
                fromUser,
                toUser: assigneeName,
                toStage: null,
                // Optimistic: the refetch replaces this with the server's answer.
                positionOverride: false,
                note,
              },
            ],
          },
        }
      })
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(qc, vars.reviewId, ctx?.previous ?? null),
  })
}

interface StageVars {
  reviewId: ReviewId
  /**
   * Who takes the second pass, on `initial → secondary` only.
   *
   * Omitted, the backend hands it to the engagement's Primary Client Contact
   * and refuses when there is none, or when that person did the first pass —
   * neither of which a reviewer can resolve without naming someone here.
   */
  secondaryReviewerId?: string | null
}

const NEXT_STAGE: Record<ReviewStage, ReviewStage | null> = {
  initial: "secondary",
  secondary: "done",
  done: null,
}

function useStageMutation(
  mutationFn: (vars: StageVars, signal: AbortSignal) => Promise<void>,
  optimistic: (prev: Review, now: string) => Review,
): MutationResult<StageVars> {
  const qc = useQueryClient()
  return useMutation<StageVars, Ctx>({
    mutationFn,
    onMutate: ({ reviewId }) => {
      const now = new Date().toISOString()
      const { previous } = patchReview(qc, reviewId, (prev) => optimistic(prev, now))
      return { previous }
    },
    onSuccess: (_data, { reviewId }) => {
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onError: (_e, vars, ctx) => rollback(qc, vars.reviewId, ctx?.previous ?? null),
  })
}

export function useReturnToPoolMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/throwback`,
        { note: null },
        { signal },
      )
    },
    (prev, now) => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        assignee: null,
        events: [
          ...prev.assignment.events,
          {
            action: "throwback",
            at: now,
            actor: "You",
            fromUser: prev.assignment.assignee?.name ?? null,
            toUser: null,
            toStage: null,
            // Optimistic: the actor is acting on their own review in the
            // ordinary case; the refetch replaces this with the server's answer.
            positionOverride: false,
            note: null,
          },
        ],
      },
    }),
  )
}

export function useAdvanceStageMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId, secondaryReviewerId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/advance-stage`,
        {
          skip_secondary: false,
          note: null,
          ...(secondaryReviewerId ? { secondary_reviewer_id: Number(secondaryReviewerId) } : {}),
        },
        { signal },
      )
    },
    (prev, now) => {
      const next = NEXT_STAGE[prev.assignment.currentStage]
      if (!next) return prev
      return {
        ...prev,
        assignment: {
          ...prev.assignment,
          currentStage: next,
          events: [
            ...prev.assignment.events,
            {
              action: "advance_stage",
              at: now,
              actor: prev.assignment.assignee?.name ?? "You",
              fromUser: null,
              toUser: null,
              toStage: next,
              // Optimistic: the actor is acting on their own review in the
              // ordinary case; the refetch replaces this with the server's answer.
              positionOverride: false,
              note: null,
            },
          ],
        },
      }
    },
  )
}

export function useSkipSecondPassMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/advance-stage`,
        { skip_secondary: true, note: null },
        { signal },
      )
    },
    (prev, now) => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        currentStage: "done",
        secondarySkipped: true,
        events: [
          ...prev.assignment.events,
          {
            action: "skip_secondary",
            at: now,
            actor: prev.assignment.assignee?.name ?? "You",
            fromUser: null,
            toUser: null,
            toStage: "done",
            // Optimistic: the actor is acting on their own review in the
            // ordinary case; the refetch replaces this with the server's answer.
            positionOverride: false,
            note: null,
          },
        ],
      },
    }),
  )
}

export function useArchiveReviewMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/archive`,
        { note: null },
        { signal },
      )
    },
    (prev, now) => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        archived: true,
        events: [
          ...prev.assignment.events,
          {
            action: "archive",
            at: now,
            actor: prev.assignment.assignee?.name ?? "You",
            fromUser: null,
            toUser: null,
            toStage: null,
            // Optimistic: the actor is acting on their own review in the
            // ordinary case; the refetch replaces this with the server's answer.
            positionOverride: false,
            note: null,
          },
        ],
      },
    }),
  )
}

/**
 * Put an archived review back in the working queue.
 *
 * The counterpart to archive, and not optional: every routing action refuses
 * an archived review with "unarchive it before routing it", so without this
 * the instruction names a control that does not exist.
 */
export function useUnarchiveReviewMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/unarchive`,
        { note: null },
        { signal },
      )
    },
    (prev, now) => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        archived: false,
        events: [
          ...prev.assignment.events,
          {
            action: "unarchive",
            at: now,
            actor: prev.assignment.assignee?.name ?? "You",
            fromUser: null,
            toUser: null,
            toStage: null,
            // Optimistic: the actor is acting on their own review in the
            // ordinary case; the refetch replaces this with the server's answer.
            positionOverride: false,
            note: null,
          },
        ],
      },
    }),
  )
}

/* ── The unread-page question ──────────────────────────────────────────── */

export type UnreadPagesDecision = "include" | "exclude" | "stop"

export interface AnswerUnreadPagesVars {
  reviewId: ReviewId
  decision: UnreadPagesDecision
}

/**
 * Answer a review parked on a page the OCR lane could not read.
 *
 * No optimistic write. The server decides what the review becomes — `pending`
 * again after `include` or `exclude`, `cancelled` after `stop` — and guessing
 * would mean showing a review as running that the server may have refused to
 * start.
 *
 * Invalidated on failure as well as success, which is not the usual shape and
 * is the point. A parked review is not polled (see `use-review-query`), so a
 * tab left open on this prompt never refreshes on its own. If a colleague
 * answers first, this tab keeps offering buttons that now return 400, and
 * without the refetch the reviewer would be told to "try again" about something
 * that will fail identically every time.
 */
export function useAnswerUnreadPagesMutation(): MutationResult<AnswerUnreadPagesVars> {
  const qc = useQueryClient()

  return useMutation<AnswerUnreadPagesVars, Ctx>({
    mutationFn: async ({ reviewId, decision }, signal) => {
      await http.post<unknown, { decision: UnreadPagesDecision }>(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/unread-pages`,
        { decision },
        { signal },
      )
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onError: (_err, vars) => {
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(vars.reviewId) })
    },
  })
}

/**
 * Take a review off the overnight lane and run it now.
 *
 * Costs roughly twice as much, which is why it is staff-only: the batch answers
 * by morning for half the money, and this is for the review that cannot wait.
 */
export function useRunNowMutation(): MutationResult<{ reviewId: ReviewId }> {
  const qc = useQueryClient()

  return useMutation<{ reviewId: ReviewId }, Ctx>({
    mutationFn: async ({ reviewId }, signal) => {
      await http.post(`/api/v1/reviews/${encodeURIComponent(reviewId)}/run-now`, {}, { signal })
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onError: (_err, vars) => {
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(vars.reviewId) })
    },
  })
}

export interface ForceOcrVars {
  reviewId: ReviewId
  /** The printed label or the governing master — not a document id. */
  side: "fpl" | "master"
  /** One-based, as the console counts pages. The server stores zero-based. */
  page: number
}

/**
 * Read one page through the OCR lane although the gate accepted it.
 *
 * For the pages automatic routing decides wrongly, not a normal path: the
 * machine decides whether a page needs OCR because a person cannot tell by
 * looking, and this is for a reviewer who can see garbled text where the gate
 * saw a healthy text layer.
 *
 * Accumulates server-side, so working through a document page by page adds to
 * the set rather than replacing it, and re-queues the review each time — an
 * override that recorded a preference without re-running would be the one
 * outcome nobody wants.
 */
export function useForceOcrMutation(): MutationResult<ForceOcrVars> {
  const qc = useQueryClient()

  return useMutation<ForceOcrVars, Ctx>({
    mutationFn: async ({ reviewId, side, page }, signal) => {
      await http.post<unknown, { side: string; pages: number[] }>(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/force-ocr`,
        { side, pages: [page] },
        { signal },
      )
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onError: (_err, vars) => {
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(vars.reviewId) })
    },
  })
}

/* ── Messages ──────────────────────────────────────────────────────────── */

export interface PostMessageVars {
  reviewId: ReviewId
  body: string
  findingId: FindingId | null
  /**
   * A failed message this send replaces, dropped as the new attempt is added.
   *
   * Retry is a fresh send of the same text, not a resumption: the first attempt
   * never reached the server, so there is nothing to resume. Without this the
   * list would carry the failed row and its retry side by side, reading as two
   * messages where the reviewer wrote one.
   */
  replaces?: MessageId
}

/**
 * What `onMutate` hands the later callbacks: which row it added, and enough to
 * undo the count it incremented. The conversation itself is not captured for
 * rollback — a failed message is marked rather than removed (see `onError`).
 */
interface PostMessageCtx {
  pendingId: MessageId
  previousConversation: ConversationData | null
  previousReview: Review | null
}

/**
 * Send a message, and show it immediately.
 *
 * The optimistic message goes into `qk.reviews.comments` — the cache the panel
 * actually reads (`useReviewCommentsQuery`). An earlier version wrote it to
 * `qk.reviews.detail` instead, which holds a `messages` field nothing renders,
 * so the sender saw nothing until a refetch landed and the feature read as a
 * slow network. If you move this, move it to the cache whose data reaches the
 * screen, and prove it with a test that asserts *before* the request settles.
 *
 * The 201 body is mapped through `toMessage` and swapped in for the pending
 * one, so the request is not followed by a refetch to learn what it already
 * returned. The socket's `comment.created` for this same message is ignored by
 * the live handler, which skips ids already in cache — otherwise one send would
 * cost three round trips for one row of text.
 */
export function usePostMessageMutation(): MutationResult<PostMessageVars> {
  const qc = useQueryClient()

  return useMutation<PostMessageVars, PostMessageCtx, CommentWire>({
    mutationFn: async ({ reviewId, body, findingId }, signal) => {
      const created = await http.post<CommentWire, { body: string; finding_id?: number }>(
        `/api/v1/reviews/${String(reviewId)}/comments`,
        { body, ...(findingId === null ? {} : { finding_id: Number(findingId) }) },
        { signal },
      )
      return created
    },
    onMutate: ({ reviewId, body, findingId, replaces }) => {
      const pendingId = `m-${localId()}` as MessageId
      const pending: Message = {
        id: pendingId,
        authorName: "You",
        authorSide: AuthorSide.REVIEWER,
        body,
        at: new Date().toISOString(),
        findingId,
        mine: true,
        unread: false,
        status: "sending",
      }

      const previousConversation = patchConversation(qc, reviewId, (prev) => ({
        ...prev,
        messages: [...prev.messages.filter((m) => m.id !== replaces), pending],
      }))

      // The rail's per-finding count lives on the review, not the conversation,
      // so this second cache is a real dependency rather than the duplication
      // the bug above looked like.
      const { previous: previousReview } = patchReview(qc, reviewId, (prev) =>
        findingId === null
          ? prev
          : {
              ...prev,
              findings: prev.findings.map((f) =>
                f.id === findingId ? { ...f, commentCount: f.commentCount + 1 } : f,
              ),
            },
      )

      return { pendingId, previousConversation, previousReview }
    },
    onSuccess: (created, { reviewId }, ctx) => {
      if (!ctx) return
      const confirmed = toMessage(created, currentUserId())
      patchConversation(qc, reviewId, (prev) => ({
        ...prev,
        // Drop any copy the server already delivered before swapping. The
        // socket echo usually beats the 201 — it is one hop against a round
        // trip — so a refetch can land the real message while this request is
        // still open. Mapping the pending row onto it without this would leave
        // two rows carrying one id.
        messages: prev.messages
          .filter((m) => m.id !== confirmed.id || m.id === ctx.pendingId)
          .map((m) => (m.id === ctx.pendingId ? confirmed : m)),
      }))
    },
    onError: (_e, { reviewId }, ctx) => {
      if (!ctx) return
      // The failed message stays, marked, so the text is not lost and Retry has
      // something to resend. Only the count — which the server never took — is
      // rolled back.
      patchConversation(qc, reviewId, (prev) => ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === ctx.pendingId ? { ...m, status: "failed" as const } : m,
        ),
      }))
      rollback(qc, reviewId, ctx.previousReview)
    },
  })
}

export interface MarkReadVars {
  reviewId: ReviewId
  findingId: FindingId | null
}

/**
 * Mark a thread — or the whole conversation — read.
 *
 * Clears the badges in both caches that carry one, because they are read from
 * different places: the filter chips in the messages panel derive theirs from
 * `Message.unread` in the conversation, while the findings rail reads
 * `Finding.unreadCount` on the review. Patching only the review left a chip
 * still badged on a thread the reviewer was looking at, which reads as the UI
 * insisting they have not seen something they just read.
 *
 * Read state is per *side*, not per user (`services/comments.is_unread_for`),
 * so clearing here clears for every reviewer. That is the backend's model, and
 * the reason this fires on opening a thread rather than on a deliberate
 * "mark read" action nobody would press.
 */
export function useMarkMessagesReadMutation(): MutationResult<MarkReadVars> {
  const qc = useQueryClient()

  return useMutation<MarkReadVars, Ctx>({
    mutationFn: async ({ reviewId, findingId }, signal) => {
      // No finding means the whole conversation, which is what the reviewer
      // did by opening it.
      await http.post<unknown, { finding_id?: number; whole_conversation?: boolean }>(
        `/api/v1/reviews/${String(reviewId)}/comments/read`,
        findingId === null ? { whole_conversation: true } : { finding_id: Number(findingId) },
        { signal },
      )
      void qc.invalidateQueries({ queryKey: qk.reviews.comments(reviewId) })
    },
    onMutate: ({ reviewId, findingId }) => {
      const clears = (m: Message): boolean => findingId === null || m.findingId === findingId
      patchConversation(qc, reviewId, (prev) => {
        const messages = prev.messages.map((m) =>
          m.unread && clears(m) ? { ...m, unread: false } : m,
        )
        const stillUnread = messages.filter((m) => m.unread).length
        return { messages, totalUnread: stillUnread }
      })

      if (!findingId) return { previous: null }
      const { previous } = patchReview(qc, reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) => (f.id === findingId ? { ...f, unreadCount: 0 } : f)),
      }))
      return { previous }
    },
    // The conversation is deliberately not rolled back: the refetch this
    // mutation triggers is the authority on read state, and a badge that
    // flickers back on a failed write is worse than one cleared a moment early.
    onError: (_e, vars, ctx) => rollback(qc, vars.reviewId, ctx?.previous ?? null),
  })
}
